import {useCallback} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {commentEditHistoryQueryKey} from '@github-ui/markdown-edit-history-viewer/fetch-comment-edit-history'
import {markdownEditHistoryMetadataQueryKey} from '@github-ui/markdown-edit-history-viewer/fetch-markdown-edit-history-metadata'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import type {PullRequestAliveData} from './use-refetch-on-alive-update'
import {descriptionQueryKey, type DescriptionData} from '../page-data/loaders/use-description-data'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useLayoutPullRequest} from '../stores/layout-selectors'

function isBodyUpdate(data: PullRequestAliveData) {
  return Boolean(data.event_updates?.body_updated)
}

export function useDescriptionLiveUpdates(channel: string): void {
  const queryClient = useQueryClient()
  const descriptionURL = usePageDataUrl(PageData.description)
  const {relayId} = useLayoutPullRequest()

  const filter = useCallback(
    (data: PullRequestAliveData) => {
      if (!isBodyUpdate(data)) return false

      const context = data.event_context
      const queryKey = descriptionQueryKey(descriptionURL)
      const publishedVersion = context?.description_body_version

      if (publishedVersion) {
        const cached = queryClient.getQueryData<DescriptionData>(queryKey)
        if (cached?.bodyVersion === publishedVersion) return false
      }

      return true
    },
    [queryClient, descriptionURL],
  )

  useInvalidateOnAliveUpdate(channel, descriptionQueryKey(descriptionURL), {filter})
  useInvalidateOnAliveUpdate(relayId ? channel : null, markdownEditHistoryMetadataQueryKey(relayId ?? ''), {
    filter: isBodyUpdate,
  })
  useInvalidateOnAliveUpdate(
    relayId ? channel : null,
    {queryKey: commentEditHistoryQueryKey(relayId ?? ''), exact: false},
    {filter: isBodyUpdate},
  )
}
